import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  CANCEL_STREAMING_SETUP,
  COMPLETE_STREAMING_SETUP,
  PREPARE_STREAMING_SETUP,
  SKIP_STREAMING_SETUP,
} from "@/graphql/mutations";
import { GET_CURRENT_USER, STREAMING_SETUP_STATUS } from "@/graphql/queries";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Monitor, 
  Settings, 
  Download, 
  CheckCircle2, 
  ArrowRight, 
  Copy, 
  Check, 
  ChevronLeft,
  Video,
  Cable
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";

interface StreamingSetupAssistantProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SHOW_BROADCASTER = false;

type SetupPath = "broadcaster" | "obs" | null;

type StreamingSetupPath = "OBS" | "BROADCASTER";

interface StreamingSetupMediaInfo {
  mimeType?: string | null;
  codec?: string | null;
  averageBitrate?: number | null;
  width?: number | null;
  height?: number | null;
  framerate?: number | null;
  channels?: number | null;
  sampleRate?: number | null;
}

interface StreamingSetupInfo {
  ingressId: string;
  roomName: string;
  path: StreamingSetupPath;
  url?: string | null;
  streamKey?: string | null;
  state?: {
    status?: string | null;
    error?: string | null;
  } | null;
  video?: StreamingSetupMediaInfo | null;
  audio?: StreamingSetupMediaInfo | null;
  hasVideo: boolean;
  hasAudio: boolean;
  verified: boolean;
  createdAt?: string | null;
  verifiedAt?: string | null;
}

interface StreamSignal {
  active: boolean;
  isStable: boolean;
}

const blankSignal: StreamSignal = {
  active: false,
  isStable: false,
};

const pathToSetupPath = (selectedPath: SetupPath): StreamingSetupPath =>
  selectedPath === "broadcaster" ? "BROADCASTER" : "OBS";

const setupToSignal = (setup?: StreamingSetupInfo | null): StreamSignal => {
  if (!setup) return blankSignal;

  const status = setup.state?.status;

  return {
    active: Boolean(setup.hasVideo || status === "ENDPOINT_BUFFERING" || status === "ENDPOINT_PUBLISHING"),
    isStable: setup.verified,
  };
};

const setupErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Flow could not create a setup stream target. Please try again.";
};

const StreamingSetupAssistant = ({
  open,
  onOpenChange,
}: StreamingSetupAssistantProps) => {
  const [step, setStep] = useState(1);
  const [path, setPath] = useState<SetupPath>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  const [setupInfo, setSetupInfo] = useState<StreamingSetupInfo | null>(null);
  const [signal, setSignal] = useState<StreamSignal>(blankSignal);
  
  const { toast } = useToast();
  const [prepareStreamingSetup, { loading: preparingSetup }] = useMutation<
    { prepareStreamingSetup: StreamingSetupInfo },
    { path: StreamingSetupPath }
  >(PREPARE_STREAMING_SETUP);
  const [cancelStreamingSetup] = useMutation(CANCEL_STREAMING_SETUP);
  const [skipStreamingSetup] = useMutation(SKIP_STREAMING_SETUP, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
  });
  const [completeStreamingSetup] = useMutation(COMPLETE_STREAMING_SETUP, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
    awaitRefetchQueries: true,
  });
  const { data: setupStatusData } = useQuery<{ streamingSetupStatus: StreamingSetupInfo }>(
    STREAMING_SETUP_STATUS,
    {
      variables: { ingressId: setupInfo?.ingressId ?? "" },
      skip: !open || step !== 4 || !setupInfo?.ingressId,
      pollInterval: setupInfo?.verified ? 0 : 2000,
      fetchPolicy: "network-only",
    }
  );

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  useEffect(() => {
    const setup = setupStatusData?.streamingSetupStatus;
    if (!setup) return;

    setSetupInfo(setup);
    setSignal(setupToSignal(setup));
    if (setup.verified) {
      setShowTroubleshooting(false);
    }
  }, [setupStatusData]);

  useEffect(() => {
    if (!open || step !== 4 || signal.active || signal.isStable) return;

    const troubleshootingTimer = window.setTimeout(() => {
      setShowTroubleshooting(true);
    }, 15000);

    return () => window.clearTimeout(troubleshootingTimer);
  }, [open, step, signal.active, signal.isStable]);

  const handlePathSelect = (selectedPath: SetupPath) => {
    setPath(selectedPath);
    setStep(2);
    localStorage.setItem("flow_streaming_path_preference", selectedPath || "");
  };

  const handleContinueToCredentials = async () => {
    if (!path) return;

    try {
      const result = await prepareStreamingSetup({
        variables: { path: pathToSetupPath(path) },
      });
      const setup = result.data?.prepareStreamingSetup;
      if (!setup?.ingressId || !setup.url || !setup.streamKey) {
        throw new Error("Streaming setup credentials were not returned");
      }

      setSetupInfo(setup);
      setSignal(setupToSignal(setup));
      setShowTroubleshooting(false);

      setCopiedToken(false);
      setCopiedUrl(false);
      setStep(3);
    } catch (error) {
      console.error("Failed to prepare streaming setup:", error);
      toast({
        title: "Setup target unavailable",
        description: setupErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string, type: "url" | "token") => {
    navigator.clipboard.writeText(text);
    if (type === "url") setCopiedUrl(true);
    else setCopiedToken(true);
    
    setTimeout(() => {
      if (type === "url") setCopiedUrl(false);
      else setCopiedToken(false);
    }, 2000);

    toast({
      title: "Copied to clipboard",
      description: `${type === "url" ? "Stream URL" : "Bearer Token"} copied.`,
    });
  };

  const handleComplete = async () => {
    if (!setupInfo?.ingressId) return;

    try {
      await completeStreamingSetup({
        variables: {
          input: {
            ingressId: setupInfo.ingressId,
            allowMissingAudio: true,
          },
        },
      });
      toast({
        title: "Setup Complete",
        description: "You're all set to start your first session.",
      });
      setSetupInfo(null);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to complete streaming setup:", error);
      toast({
        title: "Error",
        description: "Flow could not verify this setup yet. Please check the signal and try again.",
        variant: "destructive",
      });
    }
  };

  const resetSetup = () => {
    const ingressId = setupInfo?.ingressId;
    if (ingressId) {
      cancelStreamingSetup({ variables: { ingressId } }).catch((error) => {
        console.warn("Failed to cancel streaming setup:", error);
      });
    }

    setStep(1);
    setPath(null);
    setSetupInfo(null);
    setSignal(blankSignal);

    setShowTroubleshooting(false);
    setCopiedToken(false);
    setCopiedUrl(false);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (!val) resetSetup();
      onOpenChange(val);
    }}>
      <DialogContent className="sm:max-w-2xl bg-card border-border overflow-hidden p-0">
        <div className="relative h-full flex flex-col">
          {/* Progress Bar */}
          <div className="absolute top-0 left-0 w-full h-1 bg-muted overflow-hidden z-10">
            <Progress value={progress} className="h-full rounded-none bg-primary/20" />
          </div>

          <div className="p-8 space-y-6">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <DialogHeader className="p-0">
                      <DialogTitle className="text-3xl font-bold tracking-tight">Choose your video path</DialogTitle>
                      <DialogDescription className="text-lg">
                        Select how you will send video from your creative workstation to Flow.
                      </DialogDescription>
                    </DialogHeader>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mt-4">
                    {SHOW_BROADCASTER && (
                      <Card
                        className="p-5 cursor-pointer hover:border-primary/50 transition-all border-2 border-border group"
                        onClick={() => handlePathSelect("broadcaster")}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                            <Video className="w-6 h-6" />
                          </div>
                          <div className="flex-1 space-y-1">
                            <h4 className="font-bold text-lg">Flow Broadcaster (Recommended)</h4>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              Our standalone streaming app. Designed for high-fidelity review with lower latency and a cleaner video path than browser-based sharing.
                            </p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </Card>
                    )}

                    <Card
                      className="p-5 cursor-pointer hover:border-primary/50 transition-all border-2 border-border group"
                      onClick={() => handlePathSelect("obs")}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-blue-500/10 text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                          <Cable className="w-6 h-6" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <h4 className="font-bold text-lg">OBS Studio</h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            Flexible setup for professional workflows using SDI, NDI, or capture devices.
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-500 transition-colors" />
                      </div>
                    </Card>
                  </div>

                  <div className="flex justify-center pt-2">
                    <Button
                      variant="ghost"
                      className="text-xs text-muted-foreground/50 hover:text-muted-foreground hover:bg-transparent"
                      onClick={async () => {
                        await skipStreamingSetup().catch(() => {});
                        onOpenChange(false);
                      }}
                    >
                      I'll do this later
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <Button variant="ghost" onClick={() => setStep(1)} className="p-0 h-auto gap-1 text-muted-foreground hover:text-blue-500 hover:bg-transparent mb-2">
                    <ChevronLeft className="w-4 h-4" /> Back to paths
                  </Button>
                  
                  <div className="space-y-2">
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold">
                      {path === "broadcaster" ? "Install Flow Broadcaster" : "Configure OBS"}
                    </h3>
                    <p className="text-muted-foreground">
                      {path === "broadcaster" 
                        ? "Download and install our optimized streaming app for your workstation." 
                        : "Download the recommended OBS profile for the best results."}
                    </p>
                  </div>
                  </div>

                  <div className="space-y-4">
                    {path === "broadcaster" ? (
                      <Card className="p-6 border-dashed border-2 flex flex-col items-center gap-4 bg-muted/30">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <Download className="w-8 h-8 text-primary" />
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-lg">Flow Broadcaster v1.0 Beta</p>
                          <p className="text-sm text-muted-foreground">Available for macOS</p>
                        </div>
                        <Button asChild className="w-full sm:w-auto px-8 gap-2">
                          <a href="/connectors/flow-broadcaster" target="_blank" rel="noreferrer">
                            <Download className="w-4 h-4" /> Download for macOS
                          </a>
                        </Button>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        <Card className="p-6 border-dashed border-2 flex flex-col items-center gap-4 bg-muted/30">
                          <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
                            <Download className="w-8 h-8 text-blue-500" />
                          </div>
                          <div className="text-center">
                            <p className="font-semibold text-lg">Flow OBS Profile</p>
                            <p className="text-sm text-muted-foreground">Pre-configured settings for high-quality streaming.</p>
                          </div>
                          <Button asChild variant="secondary" className="w-full sm:w-auto px-8 gap-2 border border-blue-500/30">
                            <a href="/obs-profile/FlowLabs.zip" download>
                              <Download className="w-4 h-4" /> Download Profile
                            </a>
                          </Button>
                        </Card>
                        
                        <div className="bg-muted/50 p-4 rounded-lg border border-border space-y-3">
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <Settings className="w-3 h-3" /> How to import
                          </p>
                          <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
                            <li>Download the Flow OBS Profile zip.</li>
                            <li>Unzip the file.</li>
                            <li>In OBS, go to <strong>Profile</strong> &gt; <strong>Import</strong>.</li>
                            <li>Select the unzipped FlowLabs folder.</li>
                            <li>In OBS, go to <strong>Profile</strong> and select FlowLabs.</li>
                          </ol>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleContinueToCredentials} disabled={preparingSetup} className="px-8 gap-2 bg-primary text-white">
                      {preparingSetup ? "Preparing..." : "Next step"} <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <Button variant="ghost" onClick={() => setStep(2)} className="p-0 h-auto gap-1 text-muted-foreground hover:text-blue-500 hover:bg-transparent mb-2">
                    <ChevronLeft className="w-4 h-4" /> Back to installation
                  </Button>

                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold tracking-tight">
                      {path === "broadcaster" ? "Connect Flow Broadcaster" : "Connection credentials"}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {path === "broadcaster" 
                        ? "Open Flow Broadcaster and allow permissions." 
                        : "Copy these into your OBS stream settings."}
                    </p>
                  </div>

                  <div className="space-y-5">
                    {path === "broadcaster" && (
                      <div className="space-y-5">
                        <div className="bg-primary/5 p-4 rounded-xl border border-primary/10 flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10 text-primary">
                            <Video className="w-4 h-4" />
                          </div>
                          <p className="text-sm font-medium">Add a media source to your scene</p>
                        </div>

                        <div className="space-y-3">
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Setup Settings</p>
                            <p className="text-xs text-muted-foreground">In settings, enter the following stream key.</p>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <code className="flex-1 bg-muted/50 p-3 rounded-lg border border-border text-sm font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                                {setupInfo?.streamKey || "Preparing stream key..."}
                              </code>
                              <Button
                                variant="outline"
                                size="icon"
                                className="shrink-0 h-[46px] w-[46px]"
                                disabled={!setupInfo?.streamKey}
                                onClick={() => setupInfo?.streamKey && copyToClipboard(setupInfo.streamKey, "token")}
                              >
                                {copiedToken ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {path === "obs" && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Stream URL</p>
                          <div className="flex gap-2">
                            <code className="flex-1 bg-muted p-3 rounded-lg border border-border text-sm font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                              {setupInfo?.url || "Preparing stream target..."}
                            </code>
                            <Button
                              variant="outline"
                              size="icon"
                              className="shrink-0"
                              disabled={!setupInfo?.url}
                              onClick={() => setupInfo?.url && copyToClipboard(setupInfo.url, "url")}
                            >
                              {copiedUrl ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Bearer Token</p>
                          <div className="flex gap-2">
                            <code className="flex-1 bg-muted p-3 rounded-lg border border-border text-sm font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                              {setupInfo?.streamKey || "Preparing stream token..."}
                            </code>
                            <Button
                              variant="outline"
                              size="icon"
                              className="shrink-0"
                              disabled={!setupInfo?.streamKey}
                              onClick={() => setupInfo?.streamKey && copyToClipboard(setupInfo.streamKey, "token")}
                            >
                              {copiedToken ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-xl flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-orange-200/80 leading-relaxed">
                      {path === "broadcaster" 
                        ? "Save your settings and start streaming. Flow will verify the signal in the next step."
                        : "Once the credentials are added, start streaming in OBS. Flow will verify the signal in the next step."}
                    </p>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={() => setStep(4)} disabled={!setupInfo?.ingressId} className="px-8 gap-2 bg-primary text-white">
                      Verify stream <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="space-y-2 text-center py-4">
                    <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${signal.active ? 'bg-green-500/10 text-green-500' : 'bg-primary/10 text-primary animate-pulse'}`}>
                      <Monitor className="w-10 h-10" />
                    </div>
                    <h3 className="text-2xl font-bold">
                      {signal.active ? "Signal verified" : "Verifying signal"}
                    </h3>
                    <p className="text-muted-foreground">
                      {signal.active
                        ? "Your connection is verified and ready for professional review."
                        : `Start streaming in ${path === "broadcaster" ? "Flow Broadcaster" : "OBS"} to continue.`}
                    </p>
                  </div>

                  <div className="bg-muted/50 p-6 rounded-xl border border-border space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Connection Status</span>
                      <span className={`${signal.active ? 'text-green-500' : 'text-yellow-500'} font-bold flex items-center gap-1.5`}>
                        <span className={`w-2 h-2 rounded-full ${signal.active ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
                        {signal.active ? "Signal verified" : "Awaiting signal"}
                      </span>
                    </div>
                  </div>

                  {setupInfo?.state?.error && (
                    <div className="bg-destructive/10 border border-destructive/30 p-4 rounded-lg text-sm text-destructive">
                      {setupInfo.state.error}
                    </div>
                  )}



                  {showTroubleshooting && !signal.active && (
                    <div className="bg-muted/80 p-4 rounded-lg border border-border space-y-3">
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <Settings className="w-3 h-3" /> Still waiting?
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        {path === "obs" ? (
                          <>
                            <li>1. Check that the Stream URL is pasted into the Server field.</li>
                            <li>2. Check that the Bearer Token is pasted into the Bearer Token field.</li>
                            <li>3. Make sure the FlowLabs OBS profile is selected.</li>
                            <li>4. Check that your video source or capture device is active.</li>
                            <li>5. Click Start Streaming in OBS.</li>
                          </>
                        ) : (
                          <>
                            <li>1. Make sure Flow Broadcaster is open.</li>
                            <li>2. Check you have entered the stream key.</li>
                            <li>3. Check that your video source or capture device is active.</li>
                            <li>4. Click Start Streaming and keep this setup window open.</li>
                          </>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-col items-end gap-2 pt-4">
                    <Button
                      onClick={signal.active ? handleComplete : async () => {
                        await skipStreamingSetup().catch(() => {});
                        onOpenChange(false);
                      }}
                      className="px-8 gap-2 bg-primary text-white"
                      disabled={!setupInfo?.ingressId}
                    >
                      Complete setup <ArrowRight className="w-4 h-4" />
                    </Button>
                    {!signal.active && setupInfo?.ingressId && (
                      <p className="text-xs text-muted-foreground/50">
                        No signal yet — you can still complete and verify later.
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StreamingSetupAssistant;
